import { useCallback, useEffect, useRef, useState } from "react";
import type {
	ModelRatesDto,
	UsageDailyDto,
	UsageOverviewDto,
} from "@/types/ai-assistant-usage";
import { fetchModelRates, fetchUsage, fetchUsageDaily } from "./usage.api";

/** Rentang grafik harian di kartu Pemakaian AI. */
export const USAGE_CHART_DAYS = 14;

export interface AssistantUsageState {
	overview: UsageOverviewDto | null;
	daily: UsageDailyDto | null;
	rates: ModelRatesDto | null;
	loading: boolean;
	error: string | null;
	refresh: () => void;
}

/** Muat meter, grafik harian, dan tarif model; `reloadKey` berubah → muat ulang (mis. setelah batas disimpan). */
export function useAssistantUsage(reloadKey: unknown): AssistantUsageState {
	const [overview, setOverview] = useState<UsageOverviewDto | null>(null);
	const [daily, setDaily] = useState<UsageDailyDto | null>(null);
	const [rates, setRates] = useState<ModelRatesDto | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const abortRef = useRef<AbortController | null>(null);

	const refresh = useCallback(async () => {
		abortRef.current?.abort();
		const controller = new AbortController();
		abortRef.current = controller;
		setLoading(true);
		setError(null);
		try {
			const [o, d, r] = await Promise.all([
				fetchUsage(controller.signal),
				fetchUsageDaily(USAGE_CHART_DAYS, controller.signal),
				fetchModelRates(controller.signal),
			]);
			setOverview(o);
			setDaily(d);
			setRates(r);
		} catch (err) {
			if (err instanceof Error && err.name === "AbortError") return;
			setError(
				err instanceof Error ? err.message : "Gagal memuat pemakaian AI",
			);
		} finally {
			if (!controller.signal.aborted) setLoading(false);
		}
	}, []);

	// biome-ignore lint/correctness/useExhaustiveDependencies: reloadKey sengaja memicu muat ulang
	useEffect(() => {
		refresh();
		return () => abortRef.current?.abort();
	}, [refresh, reloadKey]);

	return { overview, daily, rates, loading, error, refresh };
}
